import { Injectable } from '@angular/core';
import {
    GetActiveLicensesResponse,
    LicensingApiService,
    LicenseWithDefinition,
    MainLicenseId,
} from '@kempoweroyj/ngx-licensing-api-client';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AuthService } from 'providers/auth.service';
import { EnvironmentService } from 'providers/environment.service';

import { BehaviorSubject, Observable, catchError, combineLatest, filter, from, map, of, switchMap } from 'rxjs';

const LICENSE_TYPES_BY_IMPORTANCE: MainLicenseId[] = [
    'chargeye.v2.pro',
    'chargeye.v2.advanced',
    'chargeye.v2.basic',
    'mykempower.v2.core',
    'chargeye.depot-master',
    'chargeye.depot-advanced',
    'chargeye.depot-basic',
    'chargeye.cpo-advanced',
    'chargeye.cpo-basic',
    'chargeye.trial',
    'chargeye.warranty',
    'chargeye.depotmaster',
    'chargeye.advanced',
    'chargeye.basic-trimmed',
    'chargeye.service',
    'chargeye.oem',
    'chargeye.loadbalancing',
    'chargeye.evtracking',
    'chargeye.basic', // early access
];

const licenseInfo$ = new BehaviorSubject<LicenseWithDefinition>(null);

@Injectable({
    providedIn: 'root',
})
@UntilDestroy()
/**
 * This Service is used to store license information
 */
export class LicenseInfoService {
    private _licenseInfo$ = licenseInfo$.asObservable();

    get licenseInfo(): LicenseWithDefinition {
        return licenseInfo$.value;
    }
    get licenseInfo$(): Observable<LicenseWithDefinition> {
        return this._licenseInfo$;
    }

    constructor(
        private auth: AuthService,
        private licensingApi: LicensingApiService,
        private environmentService: EnvironmentService
    ) {
        this.auth.authenticated$.subscribe({
            complete: () => {
                // Clear license info when user logs out
                licenseInfo$.next(null);
            },
        });

        // fetch license info when user logs in
        this.auth.authenticated$
            .pipe(
                filter((isAuthenticated) => isAuthenticated),
                switchMap(() =>
                    combineLatest<[string, GetActiveLicensesResponse]>([
                        this.environmentService.getAppOrigin$(),
                        this.requestMyLicenses$(),
                    ])
                ),
                map(([origin, licenseResponse]) => {
                    if (!licenseResponse) {
                        return null;
                    }
                    const mykempower = origin === 'mykempower';
                    const activeLicenses: LicenseWithDefinition[] = licenseResponse.licenses || [];
                    const licenseTypes = activeLicenses
                        .filter(
                            (l) =>
                                LICENSE_TYPES_BY_IMPORTANCE.includes(l.type) &&
                                (!mykempower || l.type.startsWith('mykempower'))
                        )
                        .map((l) => l.type);
                    const activeLicenseType = LICENSE_TYPES_BY_IMPORTANCE.find((lt) => licenseTypes.includes(lt)) || '';
                    const activeLicense = activeLicenses.find((l) => l.type === activeLicenseType);
                    return activeLicense;
                })
            )
            .subscribe({
                next: (license) => {
                    if (license) {
                        licenseInfo$.next(license);
                    }
                },
            });
    }

    requestMyLicenses$() {
        return from(this.licensingApi.getActiveLicenses()).pipe(
            catchError((err) => {
                console.error(`licensingApi#getActiveLicenses ${JSON.stringify(err, null, 2)}`);
                return of(null);
            })
        );
    }
}
